import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import {
  Button,
  Container,
  Heading,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { useHistory } from 'react-router-dom';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { ConfirmationStepCard } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCard/ConfirmationStepCard';
import {
  getSignupSteps,
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import { sdk } from 'src/utilities/sdk';
import { SignupErrorDialog } from 'src/components/selfService/features/Signup/components/SignupErrorDialog/SignupErrorDialog';
import { SignupErrorDialogVariant } from 'src/components/selfService/features/Signup/components/SignupErrorDialog/signupErrorDialog.constants';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { TermsAndConditionsControl } from 'src/components/common/TermsAndConditionsControl/TermsAndConditionsControl';

export function ConfirmationStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const [signupSteps] = useState(
    getSignupSteps(true).filter((step) => step.id !== SignupStep.SUMMARY)
  );
  const { corpId, isWelcomeBackModalPresent } = useSignupStatus();
  const [showSubmitErrorDialog, setShowSubmitErrorDialog] = useState(false);
  const [numberOfPeople, setNumberOfPeople] = useState(null);
  const { status: submissionStatus, startAsyncTask } = useAsyncTask();
  const isSubmitting = submissionStatus === AsyncTaskStatus.IN_PROGRESS;
  const isSubmissionCompleted = submissionStatus === AsyncTaskStatus.COMPLETE;
  const isSubmissionError = submissionStatus === AsyncTaskStatus.ERROR;

  const useFormProps = useForm({
    mode: 'all',
    defaultValues: {
      termsAndConditionsChecked: false,
    },
  });

  const {
    formState: { isValid },
    handleSubmit,
  } = useFormProps;

  const { push } = useHistory();

  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    if (!isWelcomeBackModalPresent) {
      // send analytics
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName: 'review',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeBackModalPresent]);

  useEffect(() => {
    if (isSubmissionCompleted) {
      const baseManagementUrl = `/corporate/management/${corpId}`;
      if (numberOfPeople > 0) {
        // go to account page with modal
        push(`${baseManagementUrl}/account?firstTime=true`);
      } else {
        // go to people page with modal
        push(`${baseManagementUrl}/people?firstTime=true`);
      }

      sendTealiumEvent({
        eventcategory: 'self-service sign up_review',
        eventaction: 'submit_cta',
        eventlabel: AnalyticsEventLabel.SUCCESS,
      });
    }

    if (isSubmissionError) {
      sendTealiumEvent({
        eventcategory: 'self-service sign up_review',
        eventaction: 'submit_cta',
        eventlabel: AnalyticsEventLabel.ERROR,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmissionCompleted, isSubmissionError]);

  async function submitCorp() {
    try {
      await startAsyncTask(() => {
        return sdk.http.post(`/corp-admin/corp/self/${corpId}/signup`, {});
      });
    } catch (error) {
      setShowSubmitErrorDialog(true);
    }
  }

  function onDataLoaded() {
    if (updateMobileStepSize) {
      updateMobileStepSize();
    }
  }

  return (
    <>
      <FormProvider {...useFormProps}>
        <form onSubmit={handleSubmit(submitCorp)}>
          <Container variant="sm">
            <Stack y="sm">
              <Heading variant="h1">Review and submit</Heading>
            </Stack>
            {signupSteps.map(({ id }, index) => {
              const isLastStep = index === signupSteps.length - 1;

              return (
                <Stack key={id} y={isLastStep ? 'md' : 'sm'}>
                  <ConfirmationStepCard
                    stepId={id}
                    onDataLoaded={onDataLoaded}
                    setNumberOfPeople={setNumberOfPeople}
                  />
                </Stack>
              );
            })}
            <TermsAndConditionsControl
              name="termsAndConditionsChecked"
              id="termsAndConditionsChecked"
              rules={{ required: true }}
            />
          </Container>
          <SignupStepFooter>
            <Button
              variant="primary"
              type="submit"
              block
              buttonSize="medium"
              disabled={isSubmitting || isSubmissionCompleted || !isValid}
              isLoading={isSubmitting}
              onClick={handleSubmit(submitCorp)}
            >
              Submit
            </Button>
          </SignupStepFooter>
        </form>
      </FormProvider>
      {showSubmitErrorDialog && (
        <SignupErrorDialog
          variant={SignupErrorDialogVariant.COMPLETION_UNSUCCESSFUL}
          onDismiss={() => setShowSubmitErrorDialog(false)}
        />
      )}
    </>
  );
}
